import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AssistConversation, AssistMessage, ToolTraceEntry, ForceCorrectInfo } from '../types';
import {
  sendAssistMessage,
  getAssistConversations,
  getAssistMessages,
  getCurrentUser,
} from '../services/api';

// AI Assist chat (replaces the old Team Chat). A Claude-app-style, multi-turn
// troubleshooting/observability companion. Drops into the SAME panel slot as
// Team Chat did (App.tsx renders it inside the fixed right aside), so we reuse
// that shell's header/close/animation vocabulary here.
//
// - user/assistant bubbles, multi-turn, a "thinking" state while awaiting a reply
// - a left rail listing the user's conversations + a "New chat" button
// - a thread-attach entry point (open with a threadRef pre-filled — e.g. from a
//   ConversationCard's "Troubleshoot this thread" launcher)
// - an inline "Reported to admin · #123" card when a reply creates an error report
// - an inline "Order updated · …" card when a reply force-corrected the staged
//   order (Revision 2), which ALSO repaints an open ConversationCard thread modal
//   via a decoupled 'sl-force-correct' window event (mirrors sl-open-assist)
//
// The caller's ROLE drives the server-side tool set; the frontend never gates
// tools — it just renders the conversation. All calls go through the axios `api`
// client (carries the MSAL bearer).

interface AIAssistProps {
  onClose: () => void;
  // When set, a NEW conversation is started attached to this SLX thread root id
  // (messages.id). Cleared by the parent after we consume it (see App.tsx) so a
  // later manual "New chat" isn't re-attached. Also used to seed the composer.
  initialThreadRef?: number | null;
  // Optional seed text for the composer (e.g. from the Error Reports inbox
  // "continue investigation in chat" action). One-shot; consumed on mount.
  initialMessage?: string;
  // When true (and a thread is attached), Sally SPEAKS FIRST on open: she greets
  // the teammate by name and explains what she needs help with on the thread. Set
  // by the "Sally needs help" auto-open path. One-shot; consumed on mount.
  initialAutoGreet?: boolean;
}

// Sentinel the backend recognizes as "the user just opened a needs-help thread —
// greet them and explain." Never persisted as a user turn (Sally speaks first).
const AUTO_GREET = '__sally_open_greeting__';

// A locally-rendered turn. Mirrors AssistMessage but tolerates optimistic /
// pending assistant turns (id may be a temporary negative number).
type Turn = AssistMessage & { pending?: boolean };

// Detect a force-correct in a turn's toolTrace (Revision 2). The assist loop
// records a force-correct as a tool call whose name is force_correct_order (or a
// close variant); we read its human-readable summary for the confirmation card.
// Returns null when no force-correct ran this turn. Defensive: the trace may be
// absent, empty, or an entry may lack a summary.
const detectForceCorrect = (
  toolTrace: ToolTraceEntry[] | undefined,
  threadRef: number | null,
): ForceCorrectInfo | null => {
  if (!Array.isArray(toolTrace)) return null;
  const entry = toolTrace.find(
    (t) => typeof t?.name === 'string' && /force[_-]?correct/i.test(t.name),
  );
  if (!entry) return null;
  const summary = typeof entry.summary === 'string' && entry.summary.trim() ? entry.summary.trim() : null;
  return { summary, threadRef };
};

const AIAssist: React.FC<AIAssistProps> = ({ onClose, initialThreadRef, initialMessage, initialAutoGreet }) => {
  const [conversations, setConversations] = useState<AssistConversation[]>([]);
  const [convLoading, setConvLoading] = useState(true);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [turnsLoading, setTurnsLoading] = useState(false);
  const [input, setInput] = useState(initialMessage || '');
  const [thinking, setThinking] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  // The thread this (new) conversation is attached to. For an existing loaded
  // conversation this follows the conversation row; for a brand-new chat it's the
  // launcher's threadRef until the first turn creates the conversation.
  const [threadRef, setThreadRef] = useState<number | null>(initialThreadRef ?? null);
  const [showRail, setShowRail] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  // Guard so the initial launcher payload (threadRef / seed message) is applied
  // exactly once, not on every prop identity change.
  const seededRef = useRef(false);

  const user = getCurrentUser();

  const loadConversations = useCallback(async () => {
    try {
      const data = await getAssistConversations();
      setConversations(data);
    } catch (err) {
      // Non-fatal: the rail just stays empty; the composer still works.
      console.warn('[ai-assist] failed to load conversations', err);
    } finally {
      setConvLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Sally SPEAKS FIRST: send the auto-greet sentinel and render ONLY her reply (no
  // user bubble — she's the one initiating). Used by the "Sally needs help" auto-
  // open so she greets by name and explains what she's stuck on. NON-FATAL.
  const autoGreet = useCallback(async (tRef: number) => {
    setSendError(null);
    setThinking(true);
    try {
      const res = await sendAssistMessage({ threadRef: tRef, message: AUTO_GREET });
      if (res.conversationId != null) {
        setActiveId(res.conversationId);
        loadConversations();
      }
      const forceCorrect = detectForceCorrect(res.toolTrace, tRef);
      const assistantTurn: Turn = {
        id: -(Date.now() + 1),
        role: 'assistant',
        content: res.reply,
        created_at: new Date().toISOString(),
        createdReportId: res.createdReportId ?? null,
        forceCorrect,
      };
      setTurns((prev) => [...prev, assistantTurn]);
    } catch (err: any) {
      setSendError(
        err?.response?.data?.error || err?.message ||
        "Sally couldn't start — type to her and she'll pick it up.");
    } finally {
      setThinking(false);
    }
  }, [loadConversations]);

  // Consume the one-shot launcher payload (threadRef + seed message + auto-greet)
  // on mount. CONTINUITY: if this thread already has a Sally conversation, LOAD it
  // (so re-opening keeps the memory of what was taught) instead of starting a fresh
  // chat every time — the fragmentation that made Sally "forget" across re-opens
  // (one thread had ended up with 8 separate 1-turn conversations). Only when there
  // is NO existing conversation do we start fresh + (if flagged) auto-greet.
  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    if (initialMessage) setInput(initialMessage);
    if (initialThreadRef == null) return;
    setThreadRef(initialThreadRef);
    (async () => {
      try {
        const convs = await getAssistConversations();
        const existing = (convs || [])
          .filter((c) => c.thread_ref === initialThreadRef)
          .sort((a, b) => (b.id || 0) - (a.id || 0))[0];
        if (existing) {
          // Continue the existing conversation — its turns load via the activeId
          // effect, so Sally has the full memory of the prior chat.
          setActiveId(existing.id);
          return;
        }
      } catch (err) {
        console.warn('[ai-assist] continuity lookup failed', err);
      }
      // No prior conversation for this thread → fresh chat (greet if flagged).
      setActiveId(null);
      setTurns([]);
      if (initialAutoGreet) autoGreet(initialThreadRef);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the turns for the active conversation.
  useEffect(() => {
    if (activeId == null) { setTurns([]); return; }
    let cancelled = false;
    setTurnsLoading(true);
    getAssistMessages(activeId)
      .then((msgs) => { if (!cancelled) setTurns(msgs); })
      .catch((err) => {
        if (!cancelled) { setTurns([]); console.warn('[ai-assist] failed to load messages', err); }
      })
      .finally(() => { if (!cancelled) setTurnsLoading(false); });
    return () => { cancelled = true; };
  }, [activeId]);

  // Keep the transcript pinned to the newest turn (and to the thinking bubble).
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, thinking]);

  const startNewChat = () => {
    setActiveId(null);
    setTurns([]);
    setThreadRef(null);
    setSendError(null);
    setShowRail(false);
  };

  const openConversation = (c: AssistConversation) => {
    setActiveId(c.id);
    setThreadRef(c.thread_ref ?? null);
    setSendError(null);
    setShowRail(false);
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || thinking) return;
    setSendError(null);

    // Optimistic user bubble + a thinking state while we await the reply.
    const optimisticUser: Turn = {
      id: -Date.now(),
      role: 'user',
      content: text,
      created_at: new Date().toISOString(),
    };
    setTurns((prev) => [...prev, optimisticUser]);
    setInput('');
    setThinking(true);
    // Tell the thread's card/modal that Sally is working now, so it shimmers live
    // (mirrors the sl-force-correct decoupled-event pattern). Cleared in finally.
    const _workTr = activeConv?.thread_ref ?? threadRef;
    if (_workTr != null) {
      window.dispatchEvent(new CustomEvent('sl-sally-working',
        { detail: { threadRef: _workTr, working: true } }));
    }

    try {
      const res = await sendAssistMessage({
        conversationId: activeId ?? undefined,
        threadRef: activeId == null && threadRef != null ? threadRef : undefined,
        message: text,
      });
      // A brand-new chat now has a server conversation id — adopt it so the next
      // turn continues the same conversation.
      if (activeId == null && res.conversationId != null) {
        setActiveId(res.conversationId);
        loadConversations();
      }
      // The thread this correction landed on: the conversation's attached thread
      // (follows the loaded conversation row) or, for a brand-new chat, the
      // launcher's threadRef we sent this turn with.
      const affectedThreadRef = activeConv?.thread_ref ?? threadRef;
      const forceCorrect = detectForceCorrect(res.toolTrace, affectedThreadRef);
      const assistantTurn: Turn = {
        id: -(Date.now() + 1),
        role: 'assistant',
        content: res.reply,
        created_at: new Date().toISOString(),
        createdReportId: res.createdReportId ?? null,
        forceCorrect,
      };
      setTurns((prev) => [...prev, assistantTurn]);
      // R2.6 #8: a force-correct edits the thread's staged SO server-side. If a
      // ConversationCard thread modal is open side-by-side, tell it to repaint
      // its Order-items preview NOW (rather than wait on its 2s poll) via the same
      // decoupled window-event pattern the app uses for sl-open-assist. The modal
      // re-fetches through the EXISTING getThreadOrderItems path — no new backend
      // call. Fire only when we know which thread changed.
      if (forceCorrect && affectedThreadRef != null) {
        window.dispatchEvent(
          new CustomEvent('sl-force-correct', { detail: { threadRef: affectedThreadRef } }),
        );
      }
    } catch (err: any) {
      // Roll the optimistic user bubble back into the composer so the rep doesn't
      // lose their message, and surface the failure inline.
      setTurns((prev) => prev.filter((t) => t.id !== optimisticUser.id));
      setInput(text);
      setSendError(err?.response?.data?.error || err?.message || 'Failed to reach the assistant. Try again.');
    } finally {
      setThinking(false);
      if (_workTr != null) {
        window.dispatchEvent(new CustomEvent('sl-sally-working',
          { detail: { threadRef: _workTr, working: false } }));
      }
    }
  };

  const activeConv = conversations.find((c) => c.id === activeId) || null;
  const headerThreadRef = activeConv?.thread_ref ?? threadRef;

  return (
    <div className="flex flex-col h-full">
      {/* Panel header — mirrors the Team Chat shell it replaces. */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={() => setShowRail((s) => !s)}
            title="Conversations"
            className={
              'p-1 rounded-md transition-colors ' +
              (showRail ? 'bg-brand-100 text-brand-700' : 'text-gray-400 hover:text-brand-700')
            }
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 leading-tight">Sally's Assist Chat</h3>
            {headerThreadRef != null && (
              <span className="text-base text-brand-700 font-medium">Thread #{headerThreadRef}</span>
            )}
          </div>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="relative flex flex-1 min-h-0">
        {/* Backdrop when the conversation rail is open — click anywhere to close. */}
        {showRail && (
          <div className="absolute inset-0 z-10 bg-gray-900/20" onClick={() => setShowRail(false)} />
        )}
        {/* Left rail: conversation list + New chat. Always an overlay slide-over
            (toggled from the header) so the transcript keeps the full panel width. */}
        <aside
          className={
            'flex-col w-64 flex-shrink-0 border-r border-gray-200 bg-gray-50 ' +
            (showRail ? 'flex absolute inset-y-0 left-0 z-20 shadow-xl' : 'hidden')
          }
        >
          <div className="p-2 border-b border-gray-200">
            <button
              onClick={startNewChat}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-brand-700 rounded-lg hover:bg-brand-800"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New chat
            </button>
          </div>
          <div className="flex-1 overflow-y-auto py-1">
            {convLoading ? (
              <div className="px-3 py-2 text-xs text-gray-400">Loading…</div>
            ) : conversations.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-400">No conversations yet</div>
            ) : (
              conversations.map((c) => (
                <button
                  key={c.id}
                  onClick={() => openConversation(c)}
                  className={
                    'block w-full text-left px-3 py-2 text-sm truncate transition-colors ' +
                    (c.id === activeId ? 'bg-brand-100 text-brand-800 font-medium' : 'text-gray-700 hover:bg-gray-100')
                  }
                  title={c.title || undefined}
                >
                  <span className="block truncate">{c.title || 'Untitled chat'}</span>
                  {c.thread_ref != null && (
                    <span className="text-[10px] text-gray-400">Thread #{c.thread_ref}</span>
                  )}
                </button>
              ))
            )}
          </div>
        </aside>

        {/* Transcript + composer. */}
        <div className="flex-1 flex flex-col min-w-0">
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
            {turnsLoading ? (
              <div className="text-sm text-gray-500">Loading conversation…</div>
            ) : turns.length === 0 && !thinking ? (
              <div className="h-full flex flex-col items-center justify-center text-center px-6">
                <div className="w-14 h-14 rounded-2xl bg-brand-100 text-brand-700 flex items-center justify-center mb-4">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <p className="font-semibold text-gray-800 mb-2">Talk it through with Sally</p>
                <p className="text-sm text-gray-500 leading-relaxed max-w-sm">
                  Attach a thread and Sally will tell you what she decided and where she got
                  stuck. Explain what she was missing and she'll remember it — then re-decide
                  the thread with what you taught her. Press Prompt AI to apply her new read.
                </p>
                {threadRef != null && (
                  <p className="mt-4 inline-flex items-center gap-1.5 text-sm text-brand-700 font-medium bg-brand-50 rounded-full px-3 py-1">
                    Attached to thread #{threadRef}
                  </p>
                )}
              </div>
            ) : (
              turns.map((t) => <TurnBubble key={t.id} turn={t} userName={user?.name || 'You'} />)
            )}

            {thinking && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm text-gray-500 inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
          </div>

          {sendError && (
            <div className="px-4 py-2 text-xs text-red-700 bg-red-50 border-t border-red-100">{sendError}</div>
          )}

          <form onSubmit={handleSend} className="p-3 border-t border-gray-200 bg-white">
            <div className="flex items-end gap-2 rounded-2xl border border-gray-300 bg-white px-2 py-1.5 focus-within:ring-2 focus-within:ring-brand-500 focus-within:border-brand-400 transition-shadow">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend(e as unknown as React.FormEvent);
                  }
                }}
                rows={1}
                placeholder={threadRef != null ? `Talk to Sally about thread #${threadRef}…` : 'Talk to Sally…'}
                className="flex-1 resize-none text-sm bg-transparent px-2 py-1.5 max-h-32 focus:outline-none placeholder:text-gray-400"
              />
              <button
                type="submit"
                disabled={thinking || !input.trim()}
                className="flex-shrink-0 px-4 py-2 text-sm font-medium text-white bg-brand-700 rounded-xl hover:bg-brand-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Send
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// One turn rendered as a chat bubble. Assistant turns are left-aligned/grey,
// user turns right-aligned/brand. When an assistant turn created an error report,
// an inline confirmation card is rendered under the bubble.
const TurnBubble: React.FC<{ turn: Turn; userName: string }> = ({ turn, userName }) => {
  if (turn.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] bg-brand-700 text-white rounded-2xl rounded-br-sm px-4 py-2.5 text-sm whitespace-pre-wrap break-words">
          {turn.content}
        </div>
      </div>
    );
  }
  // 'tool' turns are audit rows — render them as a quiet system note rather than
  // a full assistant bubble.
  if (turn.role === 'tool') {
    return (
      <div className="flex justify-start">
        <div className="max-w-[85%] text-[11px] text-gray-400 italic px-2">{turn.content}</div>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-start gap-1">
      <div className="max-w-[85%] bg-gray-100 text-gray-800 rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm whitespace-pre-wrap break-words">
        {turn.content}
      </div>
      {turn.createdReportId != null && (
        <div className="max-w-[85%] inline-flex items-center gap-1.5 text-xs font-medium text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Reported to admin · #{turn.createdReportId}
        </div>
      )}
      {/* Force-correct affordance (Revision 2): the loop edited the thread's
          staged order this turn. The reply text already explains it conversationally;
          this card is a quiet visual confirmation, mirroring the report card. */}
      {turn.forceCorrect != null && (
        <div className="max-w-[85%] inline-flex items-start gap-1.5 text-xs font-medium text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
          <svg className="w-4 h-4 flex-shrink-0 mt-px" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          <span>
            Order updated
            {turn.forceCorrect.summary ? <> · {turn.forceCorrect.summary}</> : null}
          </span>
        </div>
      )}
      {typeof turn.est_cost_usd === 'number' && turn.est_cost_usd > 0 && (
        <span className="text-[10px] text-gray-300 px-2">${turn.est_cost_usd.toFixed(4)}</span>
      )}
    </div>
  );
};

export default AIAssist;
